import { initializeApp, type FirebaseApp } from "firebase/app";
import {
  GoogleAuthProvider,
  getAuth,
  onAuthStateChanged,
  signInWithPopup,
  signOut,
  type Auth,
  type User
} from "firebase/auth";

// Firebase web config is not a secret: it identifies the project so the
// client SDK can reach the right Firebase Auth/Firestore endpoints. Access
// control is enforced by Firebase Auth (Google sign-in, domain-restricted)
// plus the server verifying ID tokens server-side — never by hiding this
// object. See https://firebase.google.com/docs/projects/api-keys.
const FIREBASE_CONFIG = {
  apiKey: "AIzaSyAt9Y3atcJQupDj7MbZngHyWuT_IpZ3yoI",
  authDomain: "controls-specification-engine.firebaseapp.com",
  projectId: "controls-specification-engine",
  storageBucket: "controls-specification-engine.firebasestorage.app",
  messagingSenderId: "611119771212",
  appId: "1:611119771212:web:86cc69a7d08aca86ba4ee9"
} as const;

const AUTOMATRY_HOSTED_DOMAIN = "automatry.com";

/**
 * Auth is enabled only when VITE_AUTH_DISABLED is explicitly baked in as the
 * literal string "false" at build time. Unset (local dev, `vite dev`) means
 * disabled, matching the server's default-disabled local behaviour so the
 * local stack and e2e suites never need a signed-in user. The production web
 * build sets VITE_AUTH_DISABLED=false explicitly.
 */
export function isAuthEnabled(): boolean {
  return import.meta.env.VITE_AUTH_DISABLED === "false";
}

let app: FirebaseApp | undefined;
let auth: Auth | undefined;

function firebaseAuth(): Auth {
  if (!auth) {
    app = initializeApp(FIREBASE_CONFIG);
    auth = getAuth(app);
  }

  return auth;
}

export interface AuthUser {
  uid: string;
  email: string | null;
  displayName: string | null;
}

const toAuthUser = (user: User | null): AuthUser | null =>
  user ? { uid: user.uid, email: user.email, displayName: user.displayName } : null;

/**
 * Subscribes to sign-in state. When auth is disabled (local dev) it reports a
 * signed-in placeholder-free `null` exactly once and never touches the
 * Firebase SDK, so local dev has zero network dependency on Firebase.
 */
export function subscribeToAuthState(
  callback: (user: AuthUser | null) => void
): () => void {
  if (!isAuthEnabled()) {
    callback(null);

    return () => {};
  }

  return onAuthStateChanged(firebaseAuth(), (user) => callback(toAuthUser(user)));
}

export async function signInWithGoogle(): Promise<void> {
  const provider = new GoogleAuthProvider();

  provider.setCustomParameters({ hd: AUTOMATRY_HOSTED_DOMAIN });
  await signInWithPopup(firebaseAuth(), provider);
}

export async function signOutUser(): Promise<void> {
  if (!isAuthEnabled()) {
    return;
  }

  await signOut(firebaseAuth());
}

/**
 * Returns a fresh ID token for attaching to API requests, or null when auth
 * is disabled or nobody is signed in. Never throws: a failed token refresh
 * degrades to an unauthenticated request, which the server will reject with
 * 401 rather than the client silently pretending to be authenticated.
 */
export async function getIdToken(): Promise<string | null> {
  if (!isAuthEnabled()) {
    return null;
  }

  const user = firebaseAuth().currentUser;

  if (!user) {
    return null;
  }

  try {
    return await user.getIdToken();
  } catch {
    return null;
  }
}
